import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CoreSessionStorage } from '../../../storage/session/CoreSessionStorage';
import { LIVE_SUPPORT_WELCOME_MESSAGE } from '../../../utils/chat';
import { fetchLiveSupportMessagesAction } from '../../apiActions/fetchLiveSupportMessagesAction';
import { fetchTableLevelMessagesAction } from '../../apiActions/fetchTableLevelMessagesAction';

import { ChatState, ChatTabType, Message, ChatSubscriptionState } from './types';

const initialState: ChatState = {
  selectedTab: ChatTabType.GameChat,
  lastMessage: null,
  gameChatMessages: [],
  liveSupportChatMessages: [],
  recentMessages: [],
  isLiveSupportChatRead: true,
  isChatClosed: CoreSessionStorage.getIsChatClosed() || true,
  displayMiniChat: CoreSessionStorage.getIsChatClosed(),
  isGameChatBlocked: false,
  isLiveSupportChatBlocked: false,
  chatSubscriptionState: 'INITIAL',
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    storeGameMessage: (state, action: PayloadAction<Message>) => {
      state.gameChatMessages.push(action.payload);
      // Limit the number of messages to 100
      state.gameChatMessages = state.gameChatMessages.slice(-100);
    },
    storeLiveSupportMessage: (state, action: PayloadAction<Message>) => {
      state.liveSupportChatMessages.push(action.payload);
    },
    clearLiveSupportMessages: (state) => {
      state.liveSupportChatMessages = [];
    },
    storeRecentMessage: (state, action: PayloadAction<Message>) => {
      state.recentMessages = [...state.recentMessages, action.payload].slice(-3);
    },
    storeLastMessage: (state, action: PayloadAction<Message>) => {
      state.lastMessage = action.payload;
    },
    setShowHideStatusOfMessage: (state, action: PayloadAction<Message>) => {
      /* We use this function when we want to show/hide a message in chat.
         This is a flag set from chat moderation team and can be set/unset for any chat message.
         With showHide value being false - we should hide the message. */
      const { messageId, showMessage, tagMessage } = action.payload;

      if (tagMessage === 'GAME_USER') {
        const messageToHide = state.gameChatMessages.find((msg) => msg.messageId === messageId);
        if (messageToHide) {
          messageToHide.showMessage = showMessage;
        }
      } else if (tagMessage === 'SUPPORT') {
        const messageToHide = state.liveSupportChatMessages.find((msg) => msg.messageId === messageId);
        if (messageToHide) {
          messageToHide.showMessage = showMessage;
        }
      } else {
        const messageToHide =
          state.liveSupportChatMessages.find((msg) => msg.messageId === messageId) ||
          state.gameChatMessages.find((msg) => msg.messageId === messageId);
        if (messageToHide) {
          messageToHide.showMessage = showMessage;
        }
      }
    },
    setLiveSupportReadStatus: (state, action: PayloadAction<boolean>) => {
      state.isLiveSupportChatRead = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<ChatTabType>) => {
      state.selectedTab = action.payload;

      if (action.payload === ChatTabType.LiveSupportChat) {
        state.isLiveSupportChatRead = true;
      }
    },
    setChatSubscriptionState: (state, action: PayloadAction<ChatSubscriptionState>) => {
      state.chatSubscriptionState = action.payload;
    },
    setIsChatClosed: (state, action: PayloadAction<boolean>) => {
      state.isChatClosed = action.payload;
    },
    setDisplayMiniChat: (state, action: PayloadAction<boolean>) => {
      CoreSessionStorage.setIsChatClosed(action.payload);
      state.displayMiniChat = action.payload;
    },
    setDealerWelcomeMessage: (state, action: PayloadAction<Message>) => {
      state.gameChatMessages.push(action.payload);
    },
    setInitialSupportMessage: (state, action: PayloadAction<Message>) => {
      if (state.liveSupportChatMessages.find((message) => message.messageId === 'initial_support_chat_message')) return;

      state.liveSupportChatMessages.unshift(action.payload);
    },
    setIsGameChatBlocked: (state, action: PayloadAction<boolean>) => {
      state.isGameChatBlocked = action.payload;
    },
    setIsLiveSupportChatBlocked: (state, action: PayloadAction<boolean>) => {
      state.isLiveSupportChatBlocked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTableLevelMessagesAction.fulfilled, (state, action) => {
        state.gameChatMessages = action.payload;
      })
      .addCase(fetchLiveSupportMessagesAction.fulfilled, (state, action) => {
        state.liveSupportChatMessages = [LIVE_SUPPORT_WELCOME_MESSAGE, ...action.payload];
      });
  },
});

export const chatActions = chatSlice.actions;
